// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import {
  AppIcon,
  type IconProps,
  type SvgIconName,
} from '@shared/ui-kit/components/app-icon';
import { Heading, type PopupsName } from '@shared/ui-kit/components';
import { Table } from '@shared/ui-kit/components/grouped-table';
import { type HistValue } from '@shared/ui-kit/components/period-picker/period-picker';
import { classNames, formatToMoney } from '@shared/ui-kit/utils';
import { isUndefined } from '@shared/utils';
import { useTranslation } from 'react-i18next';

export type KpiPeriod = Exclude<HistValue, 'MAX' | '-'>;

export interface KpiBoxesProps {
  text: string;
  value: number | string;
  cash?: number;
  numberPositions?: number;
  dataTestid?: string;
  popupName?: PopupsName;
  benchmarkScore?: number | string;
  feature?: string;
  footerBoxText?: string;
  currency?: string;
  onClick?: () => void;
  return?: number;
  returnInvestment?: number;
  period?: KpiPeriod;
  icon?: SvgIconName;
  color?: IconProps['color'];
  /** Non-positive change is favorable, so return validation colors are inverted. */
  lowerIsBetter?: boolean;
}

export const KpiBoxes = ({
  text,
  value,
  cash,
  numberPositions,
  dataTestid = 'kpi-box',
  popupName,
  benchmarkScore,
  feature,
  footerBoxText,
  currency,
  onClick,
  return: periodReturn,
  returnInvestment: periodReturnInvestment,
  period: periodKey,
  icon,
  color,
  lowerIsBetter = false,
}: KpiBoxesProps) => {
  const { t } = useTranslation();

  const isClickable = typeof onClick === 'function';

  const figure = (
    <figure
      className={classNames(
        'min-h-[80px] min-w-[130px] rounded-lg !bg-section p-8 text-graph-text md:p-5',
        {
          'relative isolate cursor-pointer overflow-hidden transition-all before:pointer-events-none before:absolute before:inset-0 before:bg-[rgba(0,0,0,0.04)] before:opacity-0 before:transition-opacity hover:before:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30':
            isClickable,
        },
      )}
      data-testid={dataTestid}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
    >
      <figcaption className="grid grid-cols-1 gap-y-1.5">
        <div className="flex items-center gap-2">
          {icon && (
            <span>
              <AppIcon icon={icon} size="xl" color={color} />
            </span>
          )}

          <Heading
            variant="h5"
            title={text}
            className="w-full justify-between !font-medium uppercase text-primary-medium max-md:!text-base"
            popupName={popupName}
            tooltip
          />
        </div>

        <p className="money-display text-lg font-semibold md:text-2xl">{value}</p>

        {!!numberPositions && (
          <p className="flex max-w-48 justify-between">
            <span>{t('glossary.no-of-positions', { ns: 'glossary' })}</span>
            <span className="money-display">{numberPositions}</span>
          </p>
        )}

        {!isUndefined(cash) && (
          <div className="inline-flex gap-x-3">
            <span>{t('glossary.cash', { ns: 'glossary' })}</span>
            <span
              className={classNames(
                {
                  'text-primary-red': cash < 0,
                  'text-positive': cash > 0,
                },
                'money-display',
              )}
            >
              {formatToMoney(cash || 0, currency)}
            </span>
          </div>
        )}

        {benchmarkScore && (
          <p className="flex flex-row justify-between">
            <span>
              {feature === 'rebalance'
                ? t('glossary.current-portfolio', { ns: 'glossary' })
                : t('glossary.benchmark', { ns: 'glossary' })}
              &nbsp;
            </span>
            <span className="money-display">{benchmarkScore}</span>
          </p>
        )}

        {footerBoxText && (
          <p className="flex flex-row justify-between">
            <span>{footerBoxText}</span>
          </p>
        )}

        {typeof periodReturn === 'number' && (
          <div
            className={classNames(
              'flex items-center gap-2 text-body font-medium',
              typeof periodReturnInvestment === 'number'
                ? 'justify-between'
                : 'justify-end',
            )}
          >
            {typeof periodReturnInvestment === 'number' && (
              <Table.Data
                value={periodReturnInvestment}
                format="currency"
                currency={currency}
                formatOptions={{
                  signDisplay: 'exceptZero',
                }}
                validate
                lowerIsBetter={lowerIsBetter}
              />
            )}

            <span className="flex items-center gap-1">
              <Table.Data
                value={periodReturn}
                format="percentage"
                formatOptions={{
                  signDisplay: 'exceptZero',
                }}
                validate
                lowerIsBetter={lowerIsBetter}
              />

              {periodKey && (
                <span className="text-primary-medium">
                  {t('glossary.kpi-vs-period', {
                    ns: 'glossary',
                    period: t(`glossary.period-${periodKey}`, {
                      ns: 'glossary',
                    }),
                  })}
                </span>
              )}
            </span>
          </div>
        )}
      </figcaption>
    </figure>
  );

  if (!isClickable) {
    return figure;
  }

  return (
    <Tooltip
      title={t('glossary.kpi-go-to', { section: text, ns: 'glossary' })}
      placement="top"
    >
      {figure}
    </Tooltip>
  );
};
